import { Suspense, useEffect, useRef, useState } from "react";
import {
	BrowserRouter,
	HashRouter,
	Navigate,
	Route,
	Routes,
	useLocation,
	useNavigate,
} from "react-router-dom";

import Info from "../Info";
import { publicBaseUrl } from "../common/envconst";
import ConditionDelayClose from "../components/Conditional/ConditionDelayClose";
import ConditionTheme from "../components/Conditional/ConditionTheme";
import Layout from "../components/Layout/Layout";
import PageTransition from "../components/PageTransition/PageTransition";
import SplashScreen from "../components/SplashScreen/SplashScreen";
import ToastContainer from "../components/ToastContainer";
import { useConfigContext } from "../contexts/ConfigContext/ConfigContext";
import AdminPage from "../pages/AdminPage/AdminPage";
import CartPage from "../pages/CartPage/CartPage";
import CashQRPage from "../pages/CashQRPage/CashQRPage";
import ErrorBoundaryPage from "../pages/ErrorBoundaryPage/ErrorBoundaryPage";
import LandingPage from "../pages/LandingPage";
import MaintenancePage from "../pages/MaintenancePage/MaintenancePage";
import OrderConfirmationPage from "../pages/OrderConfirmationPage/OrderConfirmation";
import PaymentPage from "../pages/PaymentPage/PaymentPage";
import ProductListPage from "../pages/ProductListPage/ProductListPage";
import { addDatadogAction } from "../services/datadog";
import "./App.scss";
import { useAppInit } from "./hooks";
import { PATH } from "./path";

// Multi-stall paths always use BrowserRouter (supported in all environments)
// This includes preview paths like "/preview-path/hikiniku/"
// Non-multi-stall preview builds use HashRouter for compatibility
const isMultiStallPath = publicBaseUrl.includes("/hikiniku");
const isRootPath = publicBaseUrl === "/";
const FinalRouter = isRootPath || isMultiStallPath ? BrowserRouter : HashRouter;

const SplashScreenWrapper = ({ appInitResult }: { appInitResult: any }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const [showSplash, setShowSplash] = useState(true);
	const [minTimeElapsed, setMinTimeElapsed] = useState(false);
	const { state: stallMode } = useConfigContext((state) => state.stallMode);
	const splashStartRef = useRef<number>(performance.now());
	const splashFiredRef = useRef(false);

	useEffect(() => {
		const timer = setTimeout(() => {
			setMinTimeElapsed(true);
		}, 2000); // Minimum 2 seconds splash time

		return () => clearTimeout(timer);
	}, []);

	useEffect(() => {
		// Hide splash when both minimum time has elapsed and data is loaded
		if (minTimeElapsed && appInitResult.hasData && !appInitResult.isLoading) {
			setShowSplash(false);
		}
	}, [minTimeElapsed, appInitResult.hasData, appInitResult.isLoading]);

	const handleAnimationEnd = () => {
		if (!showSplash) {
			const from = location.state?.from ?? PATH.LANDING;

			if (!splashFiredRef.current) {
				addDatadogAction("splash_to_landing", {
					durationMs: performance.now() - splashStartRef.current,
					hasData: !!appInitResult.hasData,
				});
				splashFiredRef.current = true;
			}

			navigate(from, { replace: true });
		}
	};

	return (
		<SplashScreen
			stallMode={stallMode}
			isVisible={showSplash}
			onAnimationEnd={handleAnimationEnd}
		/>
	);
};

function AppRoutes() {
	const appInitResult = useAppInit();
	const { state: stallMode } = useConfigContext((state) => state.stallMode);
	const { state: locationId } = useConfigContext((state) => state.location.id);
	const showLoadingCover = !locationId;

	return (
		<ConditionTheme currentTheme={stallMode}>
			<Suspense fallback={<div>Loading...</div>}>
				<Routes>
					<Route
						path={PATH.SPLASH}
						element={<SplashScreenWrapper appInitResult={appInitResult} />}
					/>
					<Route
						path="*"
						element={
							<ConditionDelayClose check={showLoadingCover} delayTime={5000}>
								<Layout>
									<ToastContainer />
									<PageTransition>
										<Routes>
											<Route path={PATH.HOME} element={<Navigate to={PATH.SPLASH} replace />} />
											<Route path={PATH.LANDING} element={<LandingPage />} />
											<Route path={PATH.INFO} element={<Info />} />
											<Route path={PATH.PRODUCT_LIST} element={<ProductListPage />} />
											<Route path={PATH.CART} element={<CartPage />} />
											<Route path={PATH.PAYMENT} element={<PaymentPage />} />
											<Route path={PATH.CASH_QR} element={<CashQRPage />} />
											<Route path={PATH.ORDER_CONFIRMATION} element={<OrderConfirmationPage />} />
											<Route path={PATH.ADMIN} element={<AdminPage />} />
											<Route path={PATH.MAINTENANCE} element={<MaintenancePage />} />
											<Route path="/404" element={<ErrorBoundaryPage />} />
										</Routes>
									</PageTransition>
								</Layout>
							</ConditionDelayClose>
						}
					/>
				</Routes>
			</Suspense>
		</ConditionTheme>
	);
}

function App() {
	// BrowserRouter needs basename to root the app correctly on server-side routing.
	// HashRouter (Cloud Storage static deploy) does not: hash routing ignores the pathname,
	// and adding basename causes React Router to reject URLs like /uuid=xxx that don't
	// start with the basename prefix.
	const basename = isRootPath || isMultiStallPath ? publicBaseUrl : undefined;

	return (
		<FinalRouter basename={basename}>
			<AppRoutes />
		</FinalRouter>
	);
}

export default App;
